import React, { useState } from 'react';
import {
    ICampaignDetails,
    IDeleteCampaignRequestPayload,
} from '../../interfaces/campaign.interface';
import { Button, Dropdown, InputGroup, Spinner } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import { getSelectedCampaign } from '../../store/selector/campaign.selector';
import { useCopyCampaignMutation, useDeleteCampaignMutation } from '../../store/api/campaign.api';
import {
    AddBrandCampaign,
    RemoveBrandCampaign,
    SetSelectedCampaign,
} from '../../store/slice/campaign.slice';
import { useCreateNewSessionMutation } from '../../store/api/session.api';
import { ISession } from '../../interfaces/session.interface';
import { AddSession, SetCurrentlyWorkingSession } from '../../store/slice/session.slice';
import { useNavigate } from 'react-router-dom';
import { getSessions } from '../../store/selector/session.selector';
import { getEmptySession } from '../../utils/session.utils';
import { useGetOrganizationTemplatesQuery } from '../../store/api/template.api';
import { getOrganizationId } from '../../store/selector/organization.selector';
import { setTemplateDetailsInCampaign } from '../../utils/campaign.utils';

interface IProps {
    campaign: ICampaignDetails;
    selectingPlanId: string | null;
    setSelectingPlanId: React.Dispatch<React.SetStateAction<string | null>>;
}

const CampaignPlan = ({ campaign, selectingPlanId, setSelectingPlanId }: IProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const organizationId = useAppSelector(getOrganizationId);
    const activeBrand = useAppSelector(getActiveBrand);
    const sessions = useAppSelector(getSessions);
    const [deleteCampaign, { isLoading: deleteCampaignLoading }] = useDeleteCampaignMutation();
    const [copyCampaign, { isLoading: copyCampaignLoading }] = useCopyCampaignMutation();
    const [createNewSession] = useCreateNewSessionMutation();
    const { data: organizationTemplates } = useGetOrganizationTemplatesQuery(
        organizationId ? organizationId : 0,
        {
            skip: !organizationId,
        },
    );

    const [showDropdown, setShowDropdown] = useState(false);

    const handleDelete = async () => {
        if (campaign && campaign.id) {
            const campaignId = campaign.id;
            const deletePayload: IDeleteCampaignRequestPayload = {
                campaignId: campaignId,
            };
            await deleteCampaign(deletePayload);
            dispatch(RemoveBrandCampaign(campaignId));
            setShowDropdown(false);
        }
    };

    const handleCopy = async () => {
        if (campaign && campaign.id && activeBrand) {
            const response = await copyCampaign({
                campaignId: campaign.id,
                brandId: activeBrand.id,
            });
            if (response && 'data' in response) {
                const data = response.data;
                dispatch(
                    AddBrandCampaign(
                        setTemplateDetailsInCampaign(
                            data,
                            organizationTemplates ? organizationTemplates : [],
                        ),
                    ),
                );
            }
        }
    };

    const handlePlanSelect = async () => {
        if (!activeBrand || selectingPlanId !== null) return;
        setSelectingPlanId(campaign.id);
        dispatch(SetSelectedCampaign(null));
        let session = getEmptySession(sessions);
        if (!session) {
            const response = await createNewSession(activeBrand.id);
            if (response && 'data' in response) {
                session = response.data;
                dispatch(AddSession(session));
            }
        }
        if (session) {
            dispatch(SetCurrentlyWorkingSession(session));
            dispatch(SetSelectedCampaign(campaign));
            navigate(`/campaign?q=${session.sessionKey}`);
        }
        setSelectingPlanId(null);
    };

    return (
        <div className="tw-w-[328px] tw-p-4 tw-border tw-rounded-lg">
            <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
                <div className="tw-font-semibold tw-text-purple-100 tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">
                    {campaign.name}
                </div>
                <InputGroup className="custom-dropdown !tw-w-[40px]">
                    <Dropdown
                        show={showDropdown}
                        onToggle={(isOpen) => setShowDropdown(isOpen)}
                        autoClose="outside"
                    >
                        <Dropdown.Toggle
                            variant="outline-primary"
                            className="tw-rounded-md dropdown__toggle iconbtn"
                            id="dropdown-autoclose-inside"
                        >
                            <i className="ph-bold ph-dots-three-vertical"></i>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="dropdown__toggle--menu">
                            <Dropdown.Item onClick={handleCopy}>
                                <i className="ph ph-copy me-2 lg"></i>Copy
                                {copyCampaignLoading && (
                                    <Spinner as="span" size="sm" variant="primary" />
                                )}
                            </Dropdown.Item>

                            {campaign.brand_id !== 0 && (
                                <Dropdown.Item onClick={handleDelete}>
                                    <i className="ph ph-trash me-2 lg"></i>Delete
                                    {deleteCampaignLoading && (
                                        <Spinner as="span" size="sm" variant="primary" />
                                    )}
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </InputGroup>
            </div>
            <div className="tw-my-4">
                <div className="tw-flex tw-flex-col tw-items-start tw-justify-between tw-h-[102px]">
                    <div>
                        <div className="tw-text-purple-100 tw-font-semibold">Objectives</div>
                        <div className="tw-w-[296px] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-sm">
                            {campaign.objective}
                        </div>
                    </div>
                    <div>
                        <div className="tw-text-purple-100 tw-font-semibold">Target Audience</div>
                        <div className="tw-w-[296px] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-sm">
                            {campaign.target_audience}
                        </div>
                    </div>
                </div>
            </div>
            <div className="tw-w-full tw-flex  tw-gap-x-2">
                {/* <Button
                    className="tw-bg-purple-50 tw-text-black tw-border-none"
                    onClick={handlePlanSelect}
                    disabled={selectingPlan}
                >
                    <i className="ph-bold ph-pencil-simple"></i>
                </Button> */}
                <Button
                    className="tw-grow tw-whitespace-nowrap tw-bg-purple-50 tw-text-black tw-border-none"
                    onClick={handlePlanSelect}
                    disabled={selectingPlanId === campaign.id}
                >
                    <span>Start Campaign</span>
                    {selectingPlanId === campaign.id && (
                        <Spinner className="tw-text-white tw-w-6 tw-h-6 tw-ml-2" />
                    )}
                </Button>
            </div>
        </div>
    );
};

export default CampaignPlan;
